import React, { useState, useEffect } from 'react'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import InputLabel from '@mui/material/InputLabel'
import FormControl from '@mui/material/FormControl'
import Grid from '@mui/material/Grid'
import TextField from '@mui/material/TextField'
import { useTheme } from '@mui/material/styles'
import useAddExternalUserpageStyles from './styles'
import { AddExternalUserNew } from '../../../../services/api'
import CustomAlert from '../../../../components/CustomAlert/CustomAlert'
import PlantSelection from './utils/PlantSelection'
import { sendWelcomeEmail } from './utils/emailService'

const AddExternalUserspage = ({ handleTabChange, handleUserlistTabChange }) => {
  const classes = useAddExternalUserpageStyles()
  const [email, setEmail] = React.useState('')
  const [username, setUserName] = useState('')
  const [userpassword, setUserPassWord] = useState('')
  const [contactNumber, setContactNumber] = useState('')
  const [personName, setPersonName] = React.useState([])
  const [plantList, setPlantList] = React.useState([])
  const [userType, setUserType] = React.useState('') // New state for user type
  const [alertOpen, setAlertOpen] = useState(false)
  const [alertMessage, setalertMessage] = useState('')
  const [alertSeverity, setAlertSeverity] = useState('success')
  const [isFilled, setIsFilled] = useState(false)
  const [errors, setErrors] = useState({
    username: '',
    email: '',
    userpassword: '',
    contactNumber: '',
    personName: '',
    userType: '', // New error field for user type
  })

  const token = localStorage.getItem('accessToken')

  // Updated useEffect to include userType in form validation
  useEffect(() => {
    if (
      username &&
      email &&
      userpassword &&
      contactNumber &&
      personName &&
      userType
    ) {
      setIsFilled(true)
    } else {
      setIsFilled(false)
    }
  }, [contactNumber, email, personName, username, userpassword, userType])

  const theme = useTheme()
  const ITEM_HEIGHT = 48
  const ITEM_PADDING_TOP = 8
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250,
      },
    },
  }

  const handleChangePlantName = (event) => {
    const {
      target: { value },
    } = event
    setPersonName(typeof value === 'string' ? value.split(',') : value)
    setErrors({ ...errors, personName: '' })
  }

  // New handler for user type change
  const handleUserTypeChange = (event) => {
    setUserType(event.target.value)
    setErrors({ ...errors, userType: '' })
  }

  useEffect(() => {
    const fetchPlantList = async () => {
      try {
        const response = await fetch(
          'https://staging-backend-v3-express.de.energy/api/plant'
        )
        if (response.ok) {
          const data = await response.json()

          setPlantList(data?.plants || []) // Update `data?.data` based on the API structure
        } else {
          console.error('Failed to fetch plant list:', response.statusText)
        }
      } catch (error) {
        console.error('Error while fetching plant list:', error)
      }
    }

    fetchPlantList()
  }, [])

  const handleAddUser = () => {
    if (handleInputValidation()) {
      const Data = {
        name: username,
        username: email,
        password: userpassword,
        type: userType.toLowerCase(), // Use the new userType field
        contactNumber: contactNumber,
        plantIds: personName,
      }

      AddExternalUserNew(Data, token).then(async (resp) => {
        if (resp?.data && resp?.status === 200) {
          const emailResult = await sendWelcomeEmail({
            email: email,
            name: username,
            type: userType,
            contactNumber: contactNumber,
            password: userpassword,
          })

          const type = resp?.data?.data?.type || 'User'
          // Success response
          setAlertOpen(true)
          if (emailResult.success) {
            setalertMessage(
              `${type} created successfully and welcome email sent`
            )
          } else {
            setalertMessage(
              `${type} created successfully but failed to send welcome email`
            )
          }
          setAlertSeverity('success')
          setTimeout(() => {
            handleTabChange('1', '1')
            handleUserlistTabChange('2', '2')
          }, 3000)
        } else if (
          resp?.status === 400 ||
          resp?.data?.error?.name === 'BadRequestError'
        ) {
          setAlertOpen(true)
          setalertMessage(
            resp?.data?.error?.message || 'User with this email already exists.'
          )
          setAlertSeverity('error')
        } else {
          setAlertOpen(true)
          setalertMessage('An unexpected error occurred. Please try again.')
          setAlertSeverity('error')
        }
      })
    }
  }

  const handleInputValidation = () => {
    let valid = true
    const newErrors = { ...errors }

    if (!username) {
      newErrors.username = 'Name is required'
      valid = false
    } else {
      newErrors.username = ''
    }

    if (!email) {
      newErrors.email = 'Email ID is required'
      valid = false
    } else if (!isValidEmail(email)) {
      newErrors.email = 'Email ID is invalid'
      valid = false
    } else {
      newErrors.email = ''
    }

    if (!userpassword) {
      newErrors.userpassword = 'Password is required'
      valid = false
    } else {
      newErrors.userpassword = ''
    }

    if (!contactNumber) {
      newErrors.contactNumber = 'Contact Number is required'
      valid = false
    } else {
      newErrors.contactNumber = ''
    }

    if (personName.length === 0) {
      newErrors.personName = 'Choose at least one plant'
      valid = false
    } else {
      newErrors.personName = ''
    }

    if (!userType) {
      newErrors.userType = 'User type is required'
      valid = false
    } else {
      newErrors.userType = ''
    }

    setErrors(newErrors)
    return valid
  }

  const isValidEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return re.test(String(email).toLowerCase())
  }

  const handleAlertClose = (event, reason) => {
    if (reason === 'clickaway') {
      return
    }
    setAlertOpen(false)
  }

  return (
    <div className={classes.root}>
      <CustomAlert
        open={alertOpen}
        handleClose={handleAlertClose}
        message={alertMessage}
        severity={alertSeverity}
      />
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <PlantSelection
            personName={personName}
            handleChangePlantName={handleChangePlantName}
            plantList={plantList}
            errors={errors}
            theme={theme}
            MenuProps={MenuProps}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth sx={{ m: 1 }}>
            <InputLabel id="user-type-label">User Type</InputLabel>
            <Select
              labelId="user-type-label"
              id="user-type"
              value={userType}
              onChange={handleUserTypeChange}
              error={!!errors.userType}
              label="User Type"
            >
              <MenuItem value="Client">Client</MenuItem>
              <MenuItem value="Investor">Investor</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth sx={{ m: 1 }}>
            <TextField
              id="outlined-basic"
              label="Name"
              variant="outlined"
              value={username}
              onChange={(e) => {
                setUserName(e.target.value)
                setErrors({ ...errors, username: '' })
              }}
              error={!!errors.username}
              helperText={errors.username}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth sx={{ m: 1 }}>
            <TextField
              id="outlined-basic"
              label="Contact No."
              variant="outlined"
              value={contactNumber}
              onChange={(e) => {
                setContactNumber(e.target.value)
                setErrors({ ...errors, contactNumber: '' })
              }}
              error={!!errors.contactNumber}
              helperText={errors.contactNumber}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth sx={{ m: 1 }}>
            <TextField
              id="outlined-basic"
              label="Email ID"
              variant="outlined"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
                setErrors({ ...errors, email: '' })
              }}
              error={!!errors.email}
              helperText={errors.email}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth sx={{ m: 1 }}>
            <TextField
              id="outlined-basic"
              label="Password"
              variant="outlined"
              value={userpassword}
              onChange={(e) => {
                setUserPassWord(e.target.value)
                setErrors({ ...errors, userpassword: '' })
              }}
              error={!!errors.userpassword}
              helperText={errors.userpassword}
            />
          </FormControl>
        </Grid>
      </Grid>

      <button onClick={handleAddUser} className="save_btn" disabled={!isFilled}>
        Save
      </button>
    </div>
  )
}

AddExternalUserspage.propTypes = {}

export default AddExternalUserspage
